import Store from 'store/store'

import Footer from 'components/paper/Footer'
import Logo from 'components/paper/Logo'
import Paragraph from 'components/paper/Paragraph'
import Signature from 'components/paper/Signature'

import Landscape from 'controllers/landscape/render'

export default async (props, refs, paper, render) => {
  // Landscape
  ;(() => {
    const landscape = Store.landscape.abstraction.get()
    if (!landscape) return

    const scale = Math.max(props.width / landscape.props.width, props.height / landscape.props.height)
    Landscape.render(paper, { landscape, scale })

    /**
     * As the SVG version of the Landscape is really heavy on the DOM when
     * rendering multiples formats at the same time, we use the previously
     * rendered canvas as a placeholder for preview.
     * This image will not be exported.
     * Hiding the real landscape in CSS using display:none seems good enough for
     * improving scrolling perf.
     */

    render('landscape', (
      <image
        class='landscape no-export'
        ref={paper.ref('landscapePlaceholder')}
        href={Store.landscape.image.current[props.colorSpace]}
        width={landscape.props.width * scale}
        height={landscape.props.height * scale}
      />
    ))

    paper.refs.horizontalOffset.state.disabled.set(landscape.props.width * scale <= props.width)
    paper.transformLandscape()
  })()

  // Header
  ;(() => {
    ;(window.ENV.features.logo || window.ENV.features.regions) && render('header', (
      <rect
        {...props.background}
        x='0'
        y='0'
        width='100%'
        height={props.bannerHeight}
      />
    ))

    window.ENV.features.logo && render('logo', (
      <Logo
        color={props.foreground}
        x={props.padding}
        y={props.padding}
        height={props.unit}
      />
    ))

    if (window.ENV.features.logo && window.ENV.features.regions) {
      render('signature', (
        <Signature
          color={props.foreground}
          origin='NE'
          x={props.width - props.padding}
          y={props.padding}
          width={props.width - refs.logo.state.rectangle.width - props.padding * 3}
          height={props.bannerHeight - props.padding * 2}
          fontSize={refs.logo.state.fontSize}
        />
      ))
    }
  })()

  // Footer
  props.hasLogos && render('footer', (
    <Footer
      y={props.height}
      origin='SW'
      width={props.width}
      height={props.bannerHeight}
      padding={props.padding}
      fill='white'
      logos={Store.logos.get()}
    />
  ))

  // Texts
  !Store.poster.hasNoTexts.get() && (() => {
    // Short-circuit maxFontSize computing if user has touched the fontScale slider
    const fontSize = paper.state.fontScale.get()
    if (fontSize) return iterate(fontSize)

    // Find the best layout, starting for a maxFontSize and decrementing
    let iter = 0
    let maxFontSize = Store.constants.current.MAX_FONT_SIZE
    while (maxFontSize > Store.constants.current.MIN_FONT_SIZE && ++iter < 99) {
      iterate()
      maxFontSize = refs.h1.state.fontSize

      // [h1] only: no further iterations
      if (!refs.h2.state.value && !refs.h3.state.value) break
      // [h1 + h2] only: h2 does not overflow height
      if (!refs.h3.state.value && refs.h2.state.bbox.ymax + props.padding < props.height) break
      // [h1 + h3] only: no break rule necessary
      // [h1 + h2 + h3]: h2 does not overlap h3
      if (refs.h2.state.bbox.ymax + props.padding < refs.h3.state.bbox.ymin) break

      maxFontSize -= 1
      if (maxFontSize <= 0) {
        iterate(Store.constants.current.MIN_FONT_SIZE)
        break
      }
    }

    // Update the fontScale slider to use the computed maxFontSize by default
    paper.refs.fontScale.state.value.set(maxFontSize)
    paper.refs.fontScale.state.defaultValue.set(maxFontSize)

    paper.log({ iter, maxFontSize })

    function iterate (fontSize = null) {
      const common = {
        x: props.padding,
        color: props.foreground
      }

      ;[refs.h1, refs.h2, refs.h3].forEach(ref => ref && ref.destroy())

      render('h1', (
        <Paragraph
          {...common}
          y={props.bannerHeight + props.padding * 2}
          width={props.width - props.padding * 2}
          height={(props.height / 2) - props.bannerHeight - props.padding * 2}
          fontSize={fontSize}
          maxFontSize={maxFontSize}
        >
          {Store.text.h1.get()}
        </Paragraph>
      ))

      render('h2', (
        <Paragraph
          {...common}
          y={refs.h1.state.bbox.ymax + (fontSize || refs.h1.state.fontSize) * 0.5}
          width={props.width - props.padding * 2}
          height={props.height - refs.h1.state.bbox.ymax - (fontSize || refs.h1.state.fontSize) * 0.5 - props.padding}
          // Instead of setting a hard fontSize (line refs.h1.state.fontSize / 2)
          // we give a small range to allow for wiggle room when using arnaudjuracek/drapeau
          fontSize={fontSize && (fontSize * 0.5)}
          minFontSize={refs.h1.state.fontSize * 0.4}
          maxFontSize={refs.h1.state.fontSize * 0.6}
        >
          {Store.text.h2.get()}
        </Paragraph>
      ))

      render('h3', (
        <Paragraph
          {...common}
          origin='SW'
          y={props.hasLogos
            ? props.height - props.padding - props.bannerHeight
            : props.height - props.padding}
          width={(paper.state.h3FullWidth.current ? props.width : props.width / 2) - props.padding * 2}
          height={props.height - props.padding - refs.h2.state.bbox.ymax}
          // Instead of setting a hard fontSize (line refs.h1.state.fontSize / 4)
          // we give a small range to allow for wiggle room and using arnaudjuracek/drapeau
          fontSize={fontSize && (fontSize * 0.25)}
          minFontSize={refs.h2.state.fontSize * 0.4}
          maxFontSize={refs.h2.state.fontSize * 0.6}
        >
          {Store.text.h3.get()}
        </Paragraph>
      ))
    }
  })()
}



// WEBPACK FOOTER //
// ./src/controllers/templates/default.js